(() => {
  'use strict';
  const source = document.getElementById('pw-bmsm-storefront-data');
  if (!source) return;
  let catalog;
  try { catalog = JSON.parse(source.textContent || '{}'); } catch (_) { return; }
  const rules = catalog?.version === 1 && catalog.rules && typeof catalog.rules === 'object' ? catalog.rules : {};
  const fallbackTiers = catalog?.version === 1 && Array.isArray(catalog?.fallback?.tiers) ? catalog.fallback.tiers : [];
  if (!Object.keys(rules).length && !fallbackTiers.length) return;

  const moneyFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const money = cents => moneyFormatter.format(Math.round(Number(cents) || 0) / 100);
  const motions = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const eligible = '[data-pw-bmsm-eligible="true"]';
  const targets = '[data-product-grid-item][data-pw-bmsm-eligible], block-price[data-pw-bmsm-eligible], [data-wise-option-row][data-pw-bmsm-eligible]';
  const pending = new Set();

  function countTo(element, amount, currency = 'GBP', fromAmount) {
    if (!element) return;
    const target = Math.round(Number(amount) || 0);
    const prev = motions.get(element);
    if (prev?.target === target) return;
    if (prev?.frame) cancelAnimationFrame(prev.frame);
    const format = currency === 'GBP' ? money : value =>
      new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(value / 100);
    const hasPrior = prev && Number.isFinite(prev.value);
    const start = hasPrior ? prev.value :
      (Number.isFinite(Number(fromAmount)) ? Math.round(Number(fromAmount)) : target);
    const initial = !element.textContent.trim();
    if (reducedMotion.matches || (!hasPrior && !initial) || start === target) {
      element.textContent = format(target);
      motions.set(element, { target, value: target, frame: null });
      return;
    }
    const duration = 380;
    const begun = performance.now();
    const state = { target, value: start, frame: null };
    const tick = now => {
      if (!element.isConnected) { motions.delete(element); return; }
      const progress = Math.min(1, (now - begun) / duration);
      state.value = Math.round(start + (target - start) * (1 - Math.pow(1 - progress, 3)));
      element.textContent = format(state.value);
      if (progress < 1) state.frame = requestAnimationFrame(tick);
      else { state.frame = null; state.value = target; element.textContent = format(target); }
    };
    motions.set(element, state);
    state.frame = requestAnimationFrame(tick);
  }

  window.PWBmsmCountTo = countTo;
  const ruleFor = id => rules[String(id || '')] || (fallbackTiers.length ? { tiers: fallbackTiers } : null);
  const qty = value => Math.max(0, Math.floor(Number(value) || 0));

  function details(productId, unitCents, count) {
    const rule = ruleFor(productId);
    const tiers = Array.isArray(rule?.tiers) ? rule.tiers : [];
    if (!tiers.length) return null;
    const unit = Math.max(0, Math.round(Number(unitCents) || 0));
    const quantity = qty(count);
    const tier = tiers.find(t => quantity >= Number(t.minQty || 1) &&
      (t.maxQty == null || quantity <= Number(t.maxQty)));
    const rate = Math.max(0, Math.min(100, Number(tier?.percentage || 0)));
    const normal = unit * quantity;
    const discounted = Math.round(normal * (100 - rate) / 100);
    const earliest = tiers.filter(t => Number(t.percentage || 0) > 0)
      .sort((a, b) => Number(a.minQty || 0) - Number(b.minQty || 0))[0];
    return { rate, normal, discounted, saved: normal - discounted,
      min: Number(earliest?.minQty || 1), minimumRate: Number(earliest?.percentage || 0) };
  }

  function ensurePanel(host) {
    let panel = Array.from(host.children).find(el => el.classList?.contains('pw-bmsm-saving'));
    if (panel) return panel;
    panel = document.createElement('span');
    panel.className = 'pw-bmsm-saving';
    panel.setAttribute('aria-label', 'Discounted total and savings');
    panel.innerHTML = '<span class="pw-bmsm-saving__original"></span><strong class="pw-bmsm-saving__discounted"></strong><span class="pw-bmsm-saving__detail">Save <b data-pw-bmsm-rate></b>% · <b data-pw-bmsm-cash></b></span>';
    host.appendChild(panel);
    return panel;
  }

  function updatePanel(host, data) {
    if (!host) return;
    const active = Boolean(data && data.rate > 0 && data.saved > 0);
    host.dataset.pwBmsmActive = active ? 'true' : 'false';
    if (!active) {
      const previous = Array.from(host.children).find(el => el.classList?.contains('pw-bmsm-saving'));
      if (previous) previous.hidden = true;
      return;
    }
    const panel = ensurePanel(host);
    panel.hidden = false;
    panel.querySelector('.pw-bmsm-saving__original').textContent = 'Was ' + money(data.normal);
    panel.querySelector('[data-pw-bmsm-rate]').textContent = String(data.rate);
    countTo(panel.querySelector('.pw-bmsm-saving__discounted'), data.discounted, 'GBP', data.normal);
    countTo(panel.querySelector('[data-pw-bmsm-cash]'), data.saved, 'GBP', 0);
  }

  function updateCard(card) {
    const price = card.querySelector('.pw-card-price-display');
    const input = card.querySelector('[data-pw-grid-qty-input]');
    if (!price || !input) return;
    updatePanel(price, details(card.dataset.productId, card.dataset.pwBmsmUnitCents, input.value), 'card');
  }

  function updateOption(row) {
    if (row.classList.contains('is-sold-out')) return;
    const host = row.querySelector('.wise-options__info');
    const input = row.querySelector('[data-wise-qty-input]');
    if (!host || !input) return;
    updatePanel(host, details(row.dataset.targetProductId, row.dataset.pwBmsmUnitCents, input.value), 'option');
  }

  function updateProduct(block) {
    const scope = block.closest('.page-content--product') || block.closest('.quick-shop-modal') || document;
    const input = scope.querySelector('block-quantity-selector .element-quantity-selector__input, .product__quantity input[name="quantity"], input[name="quantity"]');
    updatePanel(block, details(block.dataset.productId, block.dataset.pwBmsmUnitCents, input?.value ?? 1), 'product');
  }

  function redraw(node) {
    if (!node?.isConnected) return;
    if (node.matches('[data-product-grid-item]')) updateCard(node);
    else if (node.matches('[data-wise-option-row]')) updateOption(node);
    else if (node.matches('block-price')) updateProduct(node);
  }

  function queue(node) {
    if (!node || pending.has(node)) return;
    pending.add(node);
    requestAnimationFrame(() => {
      pending.delete(node);
      redraw(node);
    });
  }

  function scan(root) {
    if (!root) return;
    if (root.matches?.(targets)) queue(root);
    root.querySelectorAll?.(targets).forEach(queue);
  }

  function fromInput(input) {
    if (!input?.closest) return;
    const card = input.closest('[data-product-grid-item][data-pw-bmsm-eligible]');
    if (card) { queue(card); return; }
    const option = input.closest('[data-wise-option-row][data-pw-bmsm-eligible]');
    if (option) { queue(option); return; }
    const scope = input.closest('.page-content--product, .quick-shop-modal');
    if (scope) scope.querySelectorAll('block-price[data-pw-bmsm-eligible]').forEach(queue);
  }

  document.addEventListener('input', event => {
    if (event.target.matches('[data-pw-grid-qty-input],[data-wise-qty-input],.element-quantity-selector__input,input[name="quantity"]')) {
      fromInput(event.target);
    }
  }, true);

  document.addEventListener('change', event => {
    if (event.target.matches('[data-pw-grid-qty-input],[data-wise-qty-input],.element-quantity-selector__input,input[name="quantity"]')) {
      fromInput(event.target);
    }
  }, true);

  document.addEventListener('click', event => {
    if (event.target.closest('[data-pw-grid-minus],[data-pw-grid-plus],[data-wise-minus],[data-wise-plus],.element-quantity-selector__button')) {
      queueMicrotask(() => fromInput(event.target));
    }
  });

  document.addEventListener('pw:bmsm:variant-price', event => queue(event.target));
  document.addEventListener('collection:reloaded', event => scan(event.target));
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('quickshop:opened', () => scan(document.querySelector('[data-tool-tip-content]') || document));
  document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  if (document.readyState !== 'loading') scan(document);
})();